import React, { useState, useEffect, forwardRef, useRef } from 'react';
import { Chip, Collapse, Icon, TextField } from '@mui/material';
import { AutoComplete } from '../Autocomplete';
import { get, size, uniqBy, sortBy, first, noop, orderBy } from '../../lib/lodash';
import useRetrieveData from 'global/hooks/useRetrieveData';
import { useStyles } from '../form/styles';
import { ChipContainer, Hidden, InlineModal, StyledFormControl } from './styles';
import { SimpleSort } from '../sortable-list/simple';
import { Loader, useScreen } from '../../styles';
import { customSearch, ensureArray } from '../../utils';
import { Row } from '../row';
import { IconButton } from '../Button';
import Modal from '../modal';

export const getTradeName = (trade) => {
  if (!trade) {
    return '';
  }
  return trade.name || trade.defaultName;
};

export const useTradeTypes = () => {
  const trades = useRetrieveData(
    { url: '/trade-types' },
    {
      version: 'v2',
      build: ['tradeTypes'],
      useCache: {
        reload: 10,
      },
      params: {
        filter: { enabled: 1 },
      },
      transformData: (d) => {
        d = d.map((dd) => ({ ...dd, _name: getTradeName(dd).toLowerCase() }));
        return sortBy(d, '_name');
      },
    }
  );

  return trades;
};

export const TradeSelectComp = forwardRef((props, ref) => {
  const classes = useStyles({ showPosition: false });

  const [selected, setSelected] = useState(props.value || []);
  const [data, setData] = useState([]);
  const [checklist, setchecklist] = useState();
  const setByDefault = useRef(false);

  const sortable = get(props, 'sortable', false);
  const hasMutedTradeIds = Array.isArray(props.mutedTradeIds);
  const mutedTradeIds = new Set((props.mutedTradeIds || []).map((id) => String(id)));

  const formatData = (d = data) => {
    let all = [];
    const checklistTrades = get(checklist, 'tradeTypes', []).filter((t) => get(t, 'enabled', true));
    const hasChecklistTrades = !!size(checklistTrades);
    if (hasChecklistTrades) {
      const cname = get(checklist, 'name');
      checklistTrades.map((t) => {
        all.push({ ...t, groupBy: cname });
      });
    }

    all = [...all, ...d.map((dd) => ({ ...dd, groupBy: hasChecklistTrades ? 'All Types' : undefined }))];

    setData(uniqBy(orderBy(all, 'order'), 'id'));
  };

  const trades = useTradeTypes();

  const clid = get(props, 'checklist.checklistId', get(props, 'checklist.id', null));
  const clist = useRetrieveData(
    { url: `/checklists/${clid}` },
    {
      build: ['checklists'],
      useCache: {
        reload: 10,
      },
      params: {
        include: 'tradeTypes',
      },
      waitFor: clid,
      transformData: (d) => {
        const ch = first(d);
        setchecklist(ch);
        const enabledTrades = get(ch, 'tradeTypes', []).filter((t) => get(t, 'enabled', true));
        const vals = ensureArray(first(orderBy(enabledTrades, 'order')));
        setSelected((s) => {
          const shouldSet = !size(s) && !props.skipDefaults;
          if (shouldSet) {
            setByDefault.current = true;
          } else {
            setByDefault.current = false;
          }
          return shouldSet ? vals : s;
        });
        props.onChecklistTradesLoaded && props.onChecklistTradesLoaded(vals);
      },
    }
  );

  const clistloading = clid && clist.loading;

  useEffect(() => {
    formatData();
  }, [clid, checklist]);

  useEffect(() => {
    if (clid && setByDefault.current) {
      setSelected([]);
    }
  }, [clid]);

  useEffect(() => {
    formatData(trades.data);
  }, [trades.data]);

  useEffect(() => {
    props.onChange && props.onChange(selected);
  }, [selected]);

  const deleteSelected = (s, i) => {
    const sel = [...selected];
    sel.splice(i, 1);
    setSelected(sel);
    setByDefault.current = false;
  };

  const onSort = (result) => {
    setSelected(result.map((r) => ({ ...r, content: undefined })));
  };

  const renderChip = (s, i) => {
    const chipStyle = hasMutedTradeIds
      ? { opacity: mutedTradeIds.has(String(get(s, 'id'))) ? 0.6 : 1.0 }
      : undefined;

    return <Chip size="small" style={chipStyle} icon={sortable ? <Icon>drag_indicator</Icon> : undefined} label={getTradeName(s)} onDelete={props.disabled ? undefined : () => deleteSelected(s, i)} />;
  };

  // PFM-2609: in normal edit mode the selected trades render inside the
  // Autocomplete input itself (via renderTags). In view (disabled) mode and
  // in sortable mode (admin checklist editor) we still need the external
  // ChipContainer — view mode hides the input entirely, and sortable mode
  // needs the SimpleSort drag-and-drop list which the input can't provide.
  const useExternalChipContainer = props.disabled || sortable;

  const renderTagsInsideInput = (value, getTagProps) =>
    value.map((option, index) => {
      const { key, ...tagProps } = getTagProps({ index });
      const chipStyle = hasMutedTradeIds
        ? { opacity: mutedTradeIds.has(String(get(option, 'id'))) ? 0.6 : 1.0 }
        : undefined;
      return <Chip key={key} size="small" style={chipStyle} label={getTradeName(option)} {...tagProps} />;
    });

  return (
    <StyledFormControl variant="standard">
      {useExternalChipContainer && (
        <Collapse in={!!size(selected)}>
          <ChipContainer>
            {sortable && <SimpleSort onSort={onSort} items={selected.map((s, i) => ({ ...s, content: renderChip(s, i) }))} />}
            {!sortable && selected.map(renderChip)}
          </ChipContainer>
        </Collapse>
      )}

      <div className={!props.disabled ? classes.formWrap : undefined} style={props.disabled ? { display: 'none' } : undefined}>
        {clistloading && <Loader />}
        {!clistloading && (
          <AutoComplete
            filterOptions={(o, s) => customSearch(o, s, { key: getTradeName })}
            classes={{ groupLabel: classes.primaryColor }}
            topOffset={-180}
            variant="standard"
            multiple={get(props, 'multiple', true)}
            options={data}
            groupBy={(opt) => opt.groupBy}
            getOptionLabel={getTradeName}
            getOptionKey={(d) => d.id}
            disableCloseOnSelect
            disableClearable
            openOnFocus
            renderInput={(params) => <TextField variant="standard" {...props} onChange={noop} label={undefined} {...params} />}
            getOptionSelected={(option, value) => option.id === value.id}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            {...props}
            // renderTags placed AFTER {...props} so consumer spreads can't
            // re-introduce the duplicate-chip rendering we're suppressing.
            renderTags={useExternalChipContainer ? () => null : renderTagsInsideInput}
            value={selected || []}
            label={undefined}
            onChange={(e, vals) => {
              const upd = uniqBy(vals, 'id');
              setSelected(upd);
              setByDefault.current = false;
              props.setIsTradeSelectDirty?.(true);
            }}
          />
        )}
      </div>
    </StyledFormControl>
  );
});

export const TradeSelect = (props) => {
  const [selected, setSelected] = useState(props.value || []);
  const [original, setOriginal] = useState(props.value || []);
  const [state, setState] = useState({
    open: false,
  });

  const { isMobileXS } = useScreen();

  useEffect(() => {
    setSelected(props.value || []);
    setOriginal(props.value || []);
  }, [props.value]);

  const highlightOriginals = get(props, 'highlightOriginals', false);
  const hasMutedTradeIds = Array.isArray(props.mutedTradeIds);
  const mutedTradeIds = new Set((props.mutedTradeIds || []).map((id) => String(id)));

  const areEqual = highlightOriginals
    ? (selected || []).map((s) => s.id).toString() ==
      get(props, 'compare', original)
        .map((o) => o.id)
        .toString()
    : false;

  const toggleModal = () => {
    setState((s) => ({ ...s, open: !s.open }));
  };

  const onChange = (sel) => {
    setSelected(sel);
    props.onChange && props.onChange(sel);
  };

  const deleteSelected = (s, i) => {
    const sel = [...selected];
    sel.splice(i, 1);
    setSelected(sel);
    props.onChange && props.onChange(sel);
  };

  const renderChip = (s, i) => {
    const chipStyle = hasMutedTradeIds ? { opacity: mutedTradeIds.has(String(get(s, 'id'))) ? 0.6 : 1.0 } : areEqual && highlightOriginals ? { opacity: 0.6 } : { opacity: 1.0 };

    return <Chip size="small" style={chipStyle} label={getTradeName(s)} onDelete={() => deleteSelected(s, i)} />;
  };

  const onChecklistTradesLoaded = (c) => {
    setOriginal(c);
    if (!size(selected) && !props.skipDefaults) {
      onChange(c);
    }
    props.onChecklistTradesLoaded && props.onChecklistTradesLoaded(c);
  };

  const type = get(props, 'type', 'default');

  if (type === 'inlineModal') {
    return (
      <InlineModal style={props.style}>
        <Row>
          <ChipContainer>{selected.map(renderChip)}</ChipContainer>
          <IconButton size="small" icon="add" onClick={toggleModal} variant="outlined"/>
        </Row>

        <Modal fullHeight={isMobileXS} fullWidth={true} maxWidth={'sm'} open={state.open} onClose={toggleModal} title={props.modalTitle || 'Add Trade Types'}>
          <div style={{ minHeight: 600 }}>{state.open && <TradeSelectComp autoFocus {...props} value={selected} onChange={onChange} placeholder={'Select Trade Types...'} />}</div>
        </Modal>

        <div style={{ display: 'none' }}>
          <TradeSelectComp {...props} onChange={noop} onChecklistTradesLoaded={onChecklistTradesLoaded} placeholder={'Select Trade Types...'} />
        </div>
      </InlineModal>
    );
  }

  return <TradeSelectComp {...props} placeholder={'Select Trade Types...'} />;
};
